import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ShieldAlert, Search, AlertTriangle, CheckCircle2, RefreshCw, Users, ChevronLeft,
  Eye, Globe, Plus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_COLORS = {
  clear: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  review: 'bg-amber-50 text-amber-700 border-amber-200',
  match: 'bg-red-50 text-red-700 border-red-200',
  not_screened: 'bg-gray-50 text-gray-500 border-gray-200',
};

const QUALITY_COLORS = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-blue-50 text-blue-800',
};

const HitCard = ({ hit, onDecision, canDecide }) => (
  <div className={`p-4 border-l-4 ${hit.match_type === 'sanction' ? 'border-l-red-500' : hit.match_type === 'pep' ? 'border-l-amber-500' : 'border-l-blue-500'} bg-gray-50 mb-2`} data-testid={`hit-${hit.match_id}`}>
    <div className="flex items-start justify-between gap-3 mb-2">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[#1f1f1f]">{hit.matched_name}</span>
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${QUALITY_COLORS[hit.match_quality] || 'bg-gray-100 text-gray-700'}`}>{hit.match_quality}</span>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-white border border-gray-300 rounded">{hit.match_type}</span>
          <span className="text-[10px] font-mono px-2 py-0.5 bg-white border rounded">{Math.round(hit.match_score * 100)}%</span>
          {hit.id_number_match && <span className="text-[10px] uppercase px-2 py-0.5 bg-red-100 text-red-700 rounded">ID match</span>}
          {hit.dob_match && <span className="text-[10px] uppercase px-2 py-0.5 bg-amber-100 text-amber-700 rounded">DOB match</span>}
        </div>
        <p className="text-xs text-gray-500 mt-1">via <strong>{hit.provider}</strong> &middot; {hit.list_source} &middot; {hit.listing_authority}</p>
      </div>
      <span className={`text-[10px] uppercase tracking-wider font-bold ${hit.decision_recommended === 'block' ? 'text-red-600' : hit.decision_recommended === 'review' ? 'text-amber-600' : 'text-emerald-600'}`}>
        {hit.decision_recommended === 'block' ? 'BLOCK' : hit.decision_recommended === 'review' ? 'REVIEW' : 'CLEAR'}
      </span>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-600">
      {hit.aliases?.length > 0 && <div className="col-span-full"><strong>Aliases:</strong> {hit.aliases.join(', ')}</div>}
      {hit.programs?.length > 0 && <div><strong>Programs:</strong> {hit.programs.join(', ')}</div>}
      {hit.list_date && <div><strong>Listed:</strong> {hit.list_date}</div>}
      {hit.nationality && <div><strong>Nationality:</strong> {hit.nationality}</div>}
      {hit.dob && <div><strong>DOB:</strong> {hit.dob}</div>}
      {hit.id_number && <div><strong>ID:</strong> {hit.id_number}</div>}
      {hit.passport_number && <div><strong>Passport:</strong> {hit.passport_number}</div>}
      {hit.entity_type && <div><strong>Entity:</strong> {hit.entity_type}</div>}
    </div>

    {hit.pep && (
      <div className="mt-2 pt-2 border-t border-gray-200 text-xs">
        <p className="font-semibold text-amber-700">PEP details</p>
        <div className="grid grid-cols-2 gap-x-4 mt-1 text-gray-600">
          <div><strong>Category:</strong> {hit.pep.category || '-'}</div>
          <div><strong>Position:</strong> {hit.pep.position || '-'}</div>
          <div><strong>Jurisdiction:</strong> {hit.pep.jurisdiction || '-'}</div>
          {hit.pep.since && <div><strong>Since:</strong> {hit.pep.since}</div>}
        </div>
      </div>
    )}

    {hit.adverse_media && (
      <div className="mt-2 pt-2 border-t border-gray-200 text-xs">
        <p className="font-semibold text-blue-700">Adverse Media</p>
        <p className="text-gray-700 mt-1">{hit.adverse_media.title}</p>
        {hit.adverse_media.summary && <p className="text-gray-500 italic mt-1">{hit.adverse_media.summary}</p>}
        <p className="text-gray-500 mt-1">
          {hit.adverse_media.source} &middot; {hit.adverse_media.publication_date}
          {hit.adverse_media.url && <> &middot; <a href={hit.adverse_media.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">View source</a></>}
        </p>
      </div>
    )}
  </div>
);

const ScreeningCard = ({ screening, onReload, canDecide }) => {
  const [note, setNote] = useState('');
  const [acting, setActing] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null); // 'false_positive' | 'confirmed_match' | 'escalated'

  const DECISION_LABELS = {
    false_positive: { title: 'Clear as False Positive?', desc: 'The client will be re-activated. This decision is permanent and audit-logged.', btn: 'Yes, clear client', cls: 'bg-emerald-600 hover:bg-emerald-700' },
    confirmed_match: { title: 'Confirm Sanctions Match?', desc: 'The client will be BLOCKED (status=sanctions_blocked). The FIC may need to be notified. This decision is permanent and audit-logged.', btn: 'Yes, block client', cls: 'bg-red-600 hover:bg-red-700' },
    escalated: { title: 'Escalate to Management?', desc: 'A compliance-review request will be pushed to the Approval Queue and admin/manager/compliance officers will be notified. The client status becomes sanctions_escalated until the manager decides.', btn: 'Yes, escalate', cls: 'bg-amber-600 hover:bg-amber-700' },
  };

  const confirmDecision = async () => {
    if (!pendingDecision) return;
    const decision = pendingDecision;
    setActing(true);
    setPendingDecision(null);
    try {
      await api.post(`/sanctions/screenings/${screening.id}/decision`, { decision, note });
      toast.success(`Decision recorded: ${decision.replace('_', ' ')}`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setActing(false);
  };

  return (
    <div className={`p-4 border ${STATUS_COLORS[screening.status] || 'bg-gray-50 border-gray-200'} mb-4`} data-testid={`screening-${screening.id}`}>
      <div className="flex items-center gap-3 mb-3">
        {screening.status === 'match' ? <AlertTriangle className="w-5 h-5" /> : screening.status === 'review' ? <ShieldAlert className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
        <div>
          <p className="font-bold uppercase tracking-wider text-sm">{screening.status}</p>
          <p className="text-xs">{screening.full_name} &middot; {screening.screened_at?.slice(0, 16).replace('T', ' ')} &middot; {screening.providers_used?.join(', ')}</p>
        </div>
        {screening.decision && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-white border border-current rounded">
            {screening.decision.replace('_', ' ')}
          </span>
        )}
      </div>

      <div className="space-y-2">
        {screening.hits?.filter(h => !h.error).map((hit, i) => <HitCard key={i} hit={hit} />)}
      </div>

      {!screening.decision && screening.status !== 'clear' && canDecide && (
        <div className="mt-3 pt-3 border-t border-current/20">
          <Label className="text-xs">Compliance note (optional)</Label>
          <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g., Verified ID does not match — false positive" className="mt-1 mb-2 bg-white" data-testid={`decision-note-${screening.id}`} />
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setPendingDecision('false_positive')} disabled={acting} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`btn-false-positive-${screening.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" />False positive — Clear client
            </Button>
            <Button onClick={() => setPendingDecision('confirmed_match')} disabled={acting} size="sm" className="bg-red-600 hover:bg-red-700 text-white" data-testid={`btn-confirmed-${screening.id}`}>
              <AlertTriangle className="w-3 h-3 mr-1" />Confirmed match — Block
            </Button>
            <Button onClick={() => setPendingDecision('escalated')} disabled={acting} size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`btn-escalate-${screening.id}`}>
              Escalate
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation dialog (replaces browser window.confirm) */}
      <AlertDialog open={!!pendingDecision} onOpenChange={(o) => !o && setPendingDecision(null)}>
        <AlertDialogContent data-testid={`confirm-dialog-${screening.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingDecision && DECISION_LABELS[pendingDecision]?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDecision && DECISION_LABELS[pendingDecision]?.desc}
              <br /><br />
              <strong>Screening:</strong> {screening.full_name}
              {note && <><br /><strong>Note:</strong> {note}</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`confirm-cancel-${screening.id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDecision} className={pendingDecision ? DECISION_LABELS[pendingDecision]?.cls : ''} data-testid={`confirm-ok-${screening.id}`}>
              {pendingDecision && DECISION_LABELS[pendingDecision]?.btn}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ComplianceHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const canDecide = ['admin', 'manager', 'compliance_officer'].includes(user?.role);
  const canManagePEPs = ['admin', 'manager', 'compliance_officer'].includes(user?.role);

  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manual screen
  const [screenName, setScreenName] = useState('');
  const [screenId, setScreenId] = useState('');
  const [screenDob, setScreenDob] = useState('');
  const [screenResult, setScreenResult] = useState(null);
  const [screening, setScreening] = useState(false);

  // PEP
  const [peps, setPeps] = useState([]);
  const [pepSearch, setPepSearch] = useState('');
  const [newPep, setNewPep] = useState({ full_name: '', position: '', category: 'Domestic', country: 'ZA' });

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, p] = await Promise.all([
        api.get('/sanctions/stats'),
        api.get('/sanctions/screenings/recent?limit=30'),
        api.get('/sanctions/peps'),
      ]);
      setStats(s.data); setRecent(r.data.screenings || []); setPeps(p.data.peps || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleScreen = async () => {
    if (!screenName.trim()) return toast.error('Enter a name');
    setScreening(true);
    try {
      const res = await api.post('/sanctions/screen', { full_name: screenName, id_number: screenId || undefined, dob: screenDob || undefined });
      setScreenResult(res.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Screen failed'); }
    setScreening(false);
  };

  const handleAddPep = async () => {
    if (!newPep.full_name.trim()) return;
    try { await api.post('/sanctions/peps', newPep); toast.success('PEP added'); setNewPep({ full_name: '', position: '', category: 'Domestic', country: 'ZA' }); load(); }
    catch { toast.error('Add failed'); }
  };
  const [pepToDelete, setPepToDelete] = useState(null);
  const handleDeletePep = (id) => setPepToDelete(id);
  const confirmDeletePep = async () => {
    const id = pepToDelete; setPepToDelete(null);
    try { await api.delete(`/sanctions/peps/${id}`); load(); } catch {}
  };
  const handleSearchPeps = async () => {
    try { const res = await api.get(`/sanctions/peps?search=${encodeURIComponent(pepSearch)}`); setPeps(res.data.peps || []); } catch {}
  };

  if (loading) return <div className="p-8 text-gray-400">Loading compliance dashboard...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="compliance-hub">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/dashboard')} className="text-xs text-gray-500 hover:text-[#1f1f1f] flex items-center gap-1 mb-2"><ChevronLeft className="w-3 h-3" />Back to Dashboard</button>
          <h1 className="text-2xl font-bold text-[#1f1f1f]" style={{ fontFamily: 'Poppins, sans-serif' }}>Compliance &amp; Screening</h1>
          <p className="text-xs text-gray-500 mt-1">FIC Act Customer Due Diligence — Sanctions, PEP, and Adverse Media screening for KYC onboarding.</p>
        </div>
        {user?.role === 'admin' && (
          <Link to="/dashboard/admin/system/sanctions" className="text-xs text-[#C5A059] hover:underline" data-testid="link-to-config">Engine Configuration →</Link>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-list-entries"><p className="text-[10px] uppercase tracking-wider text-gray-500">List entries</p><p className="text-2xl font-bold">{stats?.lists?.reduce((s, l) => s + l.count, 0) || 0}</p><p className="text-[10px] text-gray-400">{stats?.lists?.length || 0} sources</p></div>
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-peps"><p className="text-[10px] uppercase tracking-wider text-gray-500">PEPs tracked</p><p className="text-2xl font-bold">{stats?.pep_count || 0}</p></div>
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-screenings"><p className="text-[10px] uppercase tracking-wider text-gray-500">Screenings run</p><p className="text-2xl font-bold">{stats?.total_screenings || 0}</p></div>
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-pending"><p className="text-[10px] uppercase tracking-wider text-gray-500">Pending decisions</p><p className="text-2xl font-bold text-amber-600">{stats?.pending_decisions || 0}</p></div>
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-matches"><p className="text-[10px] uppercase tracking-wider text-gray-500">Confirmed matches</p><p className="text-2xl font-bold text-red-600">{stats?.matches || 0}</p></div>
      </div>

      <Tabs defaultValue="screen">
        <TabsList>
          <TabsTrigger value="screen" data-testid="tab-screen"><Search className="w-3 h-3 mr-1" />Screen a Person</TabsTrigger>
          <TabsTrigger value="recent" data-testid="tab-recent"><Eye className="w-3 h-3 mr-1" />Recent Screenings</TabsTrigger>
          <TabsTrigger value="peps" data-testid="tab-peps"><Users className="w-3 h-3 mr-1" />PEP Watchlist</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-lists"><Globe className="w-3 h-3 mr-1" />Sanctions Lists</TabsTrigger>
        </TabsList>

        <TabsContent value="screen" className="pt-4">
          <div className="bg-white border border-gray-200 p-6 max-w-3xl space-y-4">
            <p className="text-xs text-gray-500">Screen a person against all enabled providers. Use this BEFORE creating a client account if you want to verify a name first; otherwise, every newly-created client is auto-screened.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-xs">Full name *</Label><Input value={screenName} onChange={e => setScreenName(e.target.value)} placeholder="e.g., Jane Doe" data-testid="screen-name" /></div>
              <div><Label className="text-xs">ID number (optional)</Label><Input value={screenId} onChange={e => setScreenId(e.target.value)} placeholder="13-digit SA ID" data-testid="screen-id" /></div>
              <div><Label className="text-xs">Date of birth (optional)</Label><Input type="date" value={screenDob} onChange={e => setScreenDob(e.target.value)} data-testid="screen-dob" /></div>
            </div>
            <Button onClick={handleScreen} disabled={screening} className="bg-[#1f1f1f] text-white" data-testid="run-screen">{screening ? 'Screening...' : <><Search className="w-4 h-4 mr-2" />Run Screening</>}</Button>
            {screenResult && <ScreeningCard screening={screenResult} onReload={load} canDecide={canDecide} />}
          </div>
        </TabsContent>

        <TabsContent value="recent" className="pt-4 space-y-3">
          <p className="text-xs text-gray-500">All screenings run in the last 30 records. Click a result to record a compliance decision.</p>
          {recent.length === 0 && <p className="text-gray-400 text-sm py-8 text-center">No screenings yet.</p>}
          {recent.map(s => <ScreeningCard key={s.id} screening={s} onReload={load} canDecide={canDecide} />)}
        </TabsContent>

        <TabsContent value="peps" className="pt-4">
          <div className="bg-white border border-gray-200 p-4 max-w-4xl space-y-4">
            {canManagePEPs && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 pb-3 border-b">
                <Input value={newPep.full_name} onChange={e => setNewPep({ ...newPep, full_name: e.target.value })} placeholder="Full name" data-testid="new-pep-name" className="md:col-span-2" />
                <Input value={newPep.position} onChange={e => setNewPep({ ...newPep, position: e.target.value })} placeholder="Position" />
                <Input value={newPep.category} onChange={e => setNewPep({ ...newPep, category: e.target.value })} placeholder="Category" />
                <Button onClick={handleAddPep} className="bg-[#1f1f1f] text-white" data-testid="add-pep-btn"><Plus className="w-4 h-4 mr-1" />Add PEP</Button>
              </div>
            )}
            <div className="flex gap-2">
              <Input value={pepSearch} onChange={e => setPepSearch(e.target.value)} placeholder="Search PEPs..." />
              <Button onClick={handleSearchPeps} variant="outline"><Search className="w-4 h-4" /></Button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Position</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Country</th><th></th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {peps.map(p => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-medium">{p.full_name}</td>
                    <td className="px-3 py-2 text-gray-600">{p.position || '-'}</td>
                    <td className="px-3 py-2 text-gray-600">{p.category || '-'}</td>
                    <td className="px-3 py-2 text-gray-500">{p.country}</td>
                    <td className="px-3 py-2 text-right">{canManagePEPs && <button onClick={() => handleDeletePep(p.id)} className="text-red-600" data-testid={`delete-pep-${p.id}`}><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
                {peps.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No PEPs.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="lists" className="pt-4">
          <div className="bg-white border border-gray-200 p-4 max-w-3xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Source</th><th className="px-3 py-2 text-right">Entries</th><th className="px-3 py-2 text-left">Last Ingested</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(stats?.lists || []).map(l => (
                  <tr key={l.list_source}>
                    <td className="px-3 py-2 font-medium">{l.list_source}</td>
                    <td className="px-3 py-2 text-right font-mono">{l.count}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{l.last_ingested?.slice(0, 16).replace('T', ' ') || '-'}</td>
                  </tr>
                ))}
                {(!stats?.lists?.length) && <tr><td colSpan={3} className="px-3 py-8 text-center text-gray-400">No lists ingested yet. Ask an admin to run ingestion.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* PEP delete confirmation */}
      <AlertDialog open={!!pepToDelete} onOpenChange={(o) => !o && setPepToDelete(null)}>
        <AlertDialogContent data-testid="pep-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this PEP?</AlertDialogTitle>
            <AlertDialogDescription>The entry will be removed from the PEP register. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pep-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePep} className="bg-red-600 hover:bg-red-700" data-testid="pep-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ComplianceHubPage;
export { ScreeningCard, HitCard };
